import type { MeetingType } from "@przezmorze/api-types";
import {
  type ClientFilters,
  emptyClientFilters,
  MEETING_TYPE_LABELS,
  PREDEFINED_FILTERS,
  tagFilterValue,
} from "../clientFilters";
import { formatDatePl } from "../formatDatePl";

export interface ActiveFilterChip {
  key: keyof ClientFilters;
  value: string;
  label: string;
  tooltip?: string;
}

type TagFilterKey = "tags" | "excludedTags";

type ScalarFilterKey = Exclude<keyof ClientFilters, TagFilterKey | "has">;

type ChipDefinition = (filters: ClientFilters, agentName: AgentNameResolver) => ActiveFilterChip[];

/** Resolves an agent id to a display name, falling back to the id itself when the agent is unknown. */
export type AgentNameResolver = (agentId: string) => string;

const scalarChip =
  (key: ScalarFilterKey, prefix: string, format: (value: string) => string = (value) => value): ChipDefinition =>
  (filters) => {
    const value = filters[key];
    if (!value.trim()) {
      return [];
    }
    return [{ key, value, label: `${prefix}: ${format(value)}` }];
  };

const accountManagerChip: ChipDefinition = (filters, agentName) =>
  scalarChip("accountManagerId", "Opiekun", agentName)(filters, agentName);

const tagChips =
  (key: TagFilterKey, valuePrefix: string): ChipDefinition =>
  (filters) =>
    filters[key].map((tag) => ({
      key,
      value: tagFilterValue(tag),
      label: `${tag.category}: ${valuePrefix}${tag.value}`,
    }));

const predefinedFilterChips: ChipDefinition = (filters) =>
  PREDEFINED_FILTERS.filter((filter) => filters.has.includes(filter.value)).map((filter) => ({
    key: "has",
    value: filter.value,
    label: filter.label,
    tooltip: filter.description,
  }));

const chipDefinitions: ChipDefinition[] = [
  scalarChip("query", "Szukaj", (value) => `„${value.trim()}”`),
  scalarChip("status", "Status"),
  scalarChip("travelPayStatus", "Status TravelPay"),
  accountManagerChip,
  scalarChip("voivodeship", "Województwo"),
  scalarChip("city", "Miasto"),
  tagChips("tags", ""),
  tagChips("excludedTags", "nie "),
  scalarChip("calculationFrom", "Kalkulacja od", formatDatePl),
  scalarChip("orderFrom", "Zamówienie od", formatDatePl),
  scalarChip("meetingType", "Spotkanie", (value) => MEETING_TYPE_LABELS[value as MeetingType]),
  scalarChip("meetingFrom", "Spotkanie od", formatDatePl),
  scalarChip("meetingTo", "Spotkanie do", formatDatePl),
  scalarChip("notePrzezmorzeFrom", "Notatka Przezmorze od", formatDatePl),
  scalarChip("notePrzezmorzeTo", "Notatka Przezmorze do", formatDatePl),
  scalarChip("noteTravelPayFrom", "Notatka TravelPay od", formatDatePl),
  scalarChip("noteTravelPayTo", "Notatka TravelPay do", formatDatePl),
  predefinedFilterChips,
];

export function buildActiveFilterChips(filters: ClientFilters, agentName: AgentNameResolver): ActiveFilterChip[] {
  return chipDefinitions.flatMap((definition) => definition(filters, agentName));
}

export function removeFilterValue(filters: ClientFilters, key: keyof ClientFilters, value: string): ClientFilters {
  if (key === "tags" || key === "excludedTags") {
    return { ...filters, [key]: filters[key].filter((tag) => tagFilterValue(tag) !== value) };
  }
  if (key === "has") {
    return { ...filters, has: filters.has.filter((item) => item !== value) };
  }
  return { ...filters, [key]: emptyClientFilters[key] };
}
